import { createSlice, createSelector } from '@reduxjs/toolkit';
import {
  fetchBlendedTimeseries, fetchBlendedTimeseriesV2,
  fetchSimulationData,
  fetchTimeSeriesData,
    getTimeSeriesMetric,
    getHierarchicalAttribution,
    fetchCompositeRelativeMetric
} from "../../api/timeseriesApi";



// Slice for managing time series data
const timeSeriesSlice = createSlice({
  name: 'timeSeries',
  initialState: {
    analysis: {},
    seriesWindows: {},
    seriesData: {},
    metrics: {},
    portfolios: {}, // Store portfolio data by pid
    tickers: {}, // Store ticker data by symbol
    compositeMetrics: {}, // Store composite metrics by unique key

    blendedSeries: [],
    simulations: {}, // Store simulations data by model name
    loading: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    loadingSlice: false,
    sliceError: false,
    error: null,
  },
  reducers: {
    addBlendedTimeseries: (state, action) => {
      state.blendedSeries.push(action.payload);
    },
    clearBlendedTimeseries: (state) => {
      state.blendedSeries = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimeSeriesData.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.loadingSlice=false;

        const items = Array.isArray(data) ? data : [data];

        console.log("fetchTimeSeriesData", data)
        if (items) {
          items.forEach((series) => {
            const [seriesId, seriesData] = [series.symbol, series.data];
            const [startDate, endDate] = [seriesData[0].timestamp, seriesData[seriesData.length - 1].timestamp];
            state.seriesData[seriesId] = seriesData;
            state.seriesWindows[seriesId] = {startDate, endDate};
          });
        }
      })
      .addCase(getTimeSeriesMetric.fulfilled, (state, action) => {
          const { series, timestamps, config, symbols } = action.payload;
          const { metric, window, target } = config;
          state.loadingSlice = false;

          if (!state.metrics) state.metrics = {};

          symbols.forEach((symbol, symbolIndex) => {
            if (!state.metrics[symbol]) state.metrics[symbol] = {};
            if (!state.metrics[symbol][metric]) state.metrics[symbol][metric] = {};
            if (!state.metrics[symbol][metric][target]) state.metrics[symbol][metric][target] = {};

            state.metrics[symbol][metric][target][window] = timestamps.map((timestamp, timeIndex) => {
              const value = series[timeIndex] && series[timeIndex][symbolIndex];
              return {
                timestamp,
                value: value !== undefined ? value : null
              };
            });
          });
      })
      .addCase(fetchTimeSeriesData.pending, (state)=>{
          state.loadingSlice = true;
      })
      .addCase(fetchTimeSeriesData.rejected, (state, action)=>{
            state.loadingSlice = false;
            state.error = action.payload;
            state.sliceError = true;
      })
      .addCase(fetchBlendedTimeseries.pending, (state) => {
        state.loading = 'loading';
      })
      .addCase(fetchBlendedTimeseries.fulfilled, (state, action) => {
        state.blended = action.payload;
        state.loading = 'succeeded';
      })
      .addCase(fetchBlendedTimeseries.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchSimulationData.pending, (state) => {
        state.loading = 'loading';
      })
      .addCase(fetchSimulationData.fulfilled, (state, action) => {
        const { modelName, data } = action.payload;
        state.simulations[modelName] = data;
        state.loading = 'succeeded';
      })
        .addCase(getHierarchicalAttribution.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.analysis.hierarchicalAttribution = data;
        state.loading = 'succeeded';
      })
      .addCase(fetchSimulationData.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchBlendedTimeseriesV2.pending, (state) => {
        state.loading = 'loading';
      })
      .addCase(fetchBlendedTimeseriesV2.fulfilled, (state, action) => {
        state.blendedSeries.push(action.payload);
        state.loading = 'succeeded';
      })
      .addCase(fetchBlendedTimeseriesV2.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
      })
        .addCase(fetchCompositeRelativeMetric.pending, (state, action) => {
        const { portfolioId, metricName, window } = action.meta.arg;
        const key = `${portfolioId}_${metricName}_${window}`;
        if (!state.compositeMetrics[key]) {
          state.loading = 'loading';
        }
      })
      .addCase(fetchCompositeRelativeMetric.fulfilled, (state, action) => {
        const { portfolioId, metricName, window } = action.meta.arg;
        const key = `${portfolioId}_${metricName}_${window}`;
        console.log(action.payload);
        state.compositeMetrics[key] = action.payload?.series;
        state.loading = 'succeeded';
      })
  },
});


export const selectTimeSeriesData = createSelector(
  [(state, symbol) => state.timeSeries.seriesData[symbol]],
  (series) => series
);

export const selectManyTimeSeriesData = createSelector(
  [(state, symbols) => symbols.map(symbol => ({symbol, data: state.timeSeries.seriesData[symbol]}))],
  (seriesList) => seriesList.filter(series => series.data !== undefined)
);


export const selectMetricTimeSeriesData = createSelector(
  [
    (state) => state.timeSeries.metrics,
    (state) => state.timeSeries.loadingSlice,
    (state, symbols, metric, targetSymbol, window) => ({ symbols, metric, targetSymbol, window })
  ],
  (metrics, loading, { symbols, metric, targetSymbol, window }) => {

    if (loading) {
      return { loading: true, data: [] };
    }

    if (!metrics || Object.keys(metrics).length === 0) {
      return { loading: false, data: [], error: 'No metrics data available' };
    }

    const data = symbols.map(symbol => ({
      timeseriesId: symbol,
      data: metrics[symbol]?.[metric]?.[targetSymbol]?.[window] || []
    })).filter(series => series.data && series.data.length > 0);

    if (data.length === 0) {
      return { loading: false, data: [], error: 'No data available for the selected metrics' };
    }

    return { loading: false, data };
  }
);
export const selectTimeSeriesDataByPaths = createSelector(
  [(state) => state, (_, paths) => paths],
  (state, paths) => {
    const result = paths.map(pathObj => {
      const { path, id } = pathObj;
      let data;

      try {
        data = path.split('.').reduce((obj, key) => {
          if (obj === null || obj === undefined) {
            throw new Error(`Path segment '${key}' not found`);
          }
          return obj[key];
        }, state);
      } catch (error) {
        console.warn(`Error accessing path '${path}': ${error.message}`);
        data = null;
      }

      return {
        timeseriesId: id,
        data: Array.isArray(data) ? data : [],
        error: data === null ? `Data not found for path: ${path}` : null
      };
    });

    const validData = result.filter(item => item.data.length > 0);
    const errors = result.filter(item => item.error).map(item => item.error);

    if (validData.length === 0) {
      return {
        loading: false,
        data: [],
        error: errors.length > 0 ? errors.join('; ') : 'No data available for the selected paths'
      };
    }

    // Returning stable references for the result object
    return {
      loading: false,
      data: validData,
      partialData: validData.length < result.length,
      errors: errors.length > 0 ? errors : null
    };
  }
);

export const selectCompositeRelativeMetric = createSelector(
  [
    (state) => state.timeSeries.compositeMetrics,
    (state, portfolioId, metricName, window) => `${portfolioId}_${metricName}_${window}`
  ],
  (compositeMetrics, key) => compositeMetrics[key] || []
);



export const { addBlendedTimeseries, clearBlendedTimeseries } = timeSeriesSlice.actions;
export default timeSeriesSlice.reducer;
